import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import {
  createStaffInvitation,
  getAllStaffInvitations,
  deleteStaffInvitation,
  sendStaffInvitationEmail,
} from '../../services/staffInvitations';
import { getAllStaffUsers, deactivateStaffMember, deleteStaffMember, type AdminUserData } from '../../services/admin';
import type { StaffInvitation, StaffRole } from '../../types/user';

type StaffFilter = 'all' | 'admin' | 'support';

const ROLE_LABELS: Record<string, string> = {
  root: 'Super Admin',
  admin: 'Admin',
  support: 'Support',
  accounts: 'Accounts',
  sales: 'Sales',
};

const ROLE_COLORS: Record<string, string> = {
  root: 'bg-purple-100 text-purple-700',
  admin: 'bg-indigo-100 text-indigo-700',
  support: 'bg-blue-100 text-blue-700',
  accounts: 'bg-emerald-100 text-emerald-700',
  sales: 'bg-orange-100 text-orange-700',
};

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export default function AdminStaff() {
  const { currentUser, userProfile } = useAuth();
  const { showSuccess, showError } = useToast();

  const [staffUsers, setStaffUsers] = useState<AdminUserData[]>([]);
  const [invitations, setInvitations] = useState<StaffInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [staffFilter, setStaffFilter] = useState<StaffFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Invite form state
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('admin');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [all, staff] = await Promise.all([
        getAllStaffInvitations(),
        getAllStaffUsers(),
      ]);
      // Sort: pending first, then by createdAt desc
      all.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (b.status === 'pending' && a.status !== 'pending') return 1;
        return b.createdAt.localeCompare(a.createdAt);
      });
      // Sort: root first, then admin, then support, then accounts, then sales, then by createdAt desc
      const roleOrder = ['root', 'admin', 'support', 'accounts', 'sales'];
      staff.sort((a, b) => {
        const ai = roleOrder.indexOf(a.role ?? '');
        const bi = roleOrder.indexOf(b.role ?? '');
        if (ai !== bi) return ai - bi;
        return b.createdAt.localeCompare(a.createdAt);
      });
      setInvitations(all);
      setStaffUsers(staff);
    } catch { showError('Failed to load staff data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !currentUser || !userProfile) return;
    setSubmitting(true);
    try {
      const inv = await createStaffInvitation(
        email.trim(),
        role,
        currentUser.uid,
        userProfile.displayName || 'Root'
      );
      showSuccess(`Invitation sent to ${inv.email}`);
      // In a real app you'd trigger an email here via Cloud Function
      setEmail('');
      setShowForm(false);
      load();
    } catch { showError('Failed to create invitation'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (inv: StaffInvitation) => {
    if (!confirm(`Delete invitation for ${inv.email}?`)) return;
    try {
      await deleteStaffInvitation(inv.id);
      showSuccess('Invitation deleted');
      load();
    } catch { showError('Failed to delete invitation'); }
  };

  const handleDeactivate = async (user: AdminUserData) => {
    const nextActive = user.isActive === false ? true : false;
    const action = nextActive ? 'reactivate' : 'deactivate';
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${user.displayName || user.email}?`)) return;
    try {
      await deactivateStaffMember(user.uid, nextActive);
      showSuccess(`${user.displayName || user.email} ${nextActive ? 'reactivated' : 'deactivated'}`);
      load();
    } catch { showError(`Failed to ${action} staff member`); }
  };

  const handleDeleteStaff = async (user: AdminUserData) => {
    if (!confirm(`Permanently delete ${user.displayName || user.email}? This cannot be undone.\n\nHistorical messages and audit log entries will be preserved.`)) return;
    try {
      await deleteStaffMember(user.uid);
      showSuccess(`${user.displayName || user.email} deleted`);
      load();
    } catch { showError('Failed to delete staff member'); }
  };

  const inviteLink = (token: string) =>
    `${window.location.origin}/staff-invite/${token}`;

  const copyLink = (token: string) => {
    navigator.clipboard.writeText(inviteLink(token));
    showSuccess('Invite link copied');
  };

  const handleSendEmail = async (inv: StaffInvitation) => {
    try {
      await sendStaffInvitationEmail(inv.id);
      showSuccess(`Invitation email sent to ${inv.email}`);
    } catch { showError('Failed to send invitation email'); }
  };

  const filteredStaff = staffUsers.filter(user => {
    const matchesSearch =
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (staffFilter === 'admin') return user.role === 'admin' || user.role === 'root';
    if (staffFilter === 'support') return user.role === 'support';
    return true;
  });

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Staff Management</h1>
            <p className="text-gray-600">Manage admin and support team members</p>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="px-4 py-2.5 bg-[#1A6B8A] hover:bg-[#0F4A63] text-white text-[13px] font-bold rounded-xl transition-colors btn-press"
          >
            + Invite staff
          </button>
        </div>
      </div>

      {/* Invite form */}
      {showForm && (
        <form onSubmit={handleInvite} className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm px-6 py-5 mb-6">
          <h2 className="text-[14px] font-bold text-[#111827] mb-4">New staff invitation</h2>
          <div className="flex gap-3 mb-4">
            <div className="flex-1">
              <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">EMAIL</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="staff@example.com"
                className="w-full px-3.5 py-2.5 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] text-[#111827] focus:outline-none focus:border-[#1A6B8A] input-glow"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">ROLE</label>
              <select
                value={role}
                onChange={e => setRole(e.target.value as StaffRole)}
                className="h-[42px] px-3.5 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] text-[#111827] focus:outline-none focus:border-[#1A6B8A] bg-white input-glow"
              >
                <option value="admin">Admin</option>
                <option value="support">Support</option>
                <option value="accounts">Accounts</option>
                <option value="sales">Sales</option>
              </select>
            </div>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mb-4">
            Copy the invite link after creating and share it manually with the staff member.
          </p>
          <div className="flex gap-2.5">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] font-semibold text-[#374151] hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="flex-1 py-2.5 bg-[#1A6B8A] hover:bg-[#0F4A63] text-white text-[13px] font-bold rounded-[9px] transition-colors disabled:opacity-60 btn-press">
              {submitting ? 'Creating…' : 'Create invitation'}
            </button>
          </div>
        </form>
      )}

      {/* Staff accounts */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Team members</h2>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setStaffFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${staffFilter === 'all' ? 'bg-[#1A6B8A] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            >
              All Staff ({staffUsers.length})
            </button>
            <button
              onClick={() => setStaffFilter('admin')}
              className={`px-4 py-2 rounded-lg font-medium transition ${staffFilter === 'admin' ? 'bg-[#1A6B8A] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            >
              Admin
            </button>
            <button
              onClick={() => setStaffFilter('support')}
              className={`px-4 py-2 rounded-lg font-medium transition ${staffFilter === 'support' ? 'bg-[#1A6B8A] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            >
              Support
            </button>
          </div>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A6B8A] input-glow"
          />
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400 text-sm">Loading…</div>
          ) : filteredStaff.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-gray-400 text-sm">No staff accounts found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStaff.map(user => {
                    const isDeactivated = user.isActive === false;
                    const isSelf = user.uid === currentUser?.uid;
                    const isRootUser = user.role === 'root';
                    const canAct = !isSelf && !isRootUser;

                    return (
                      <tr key={user.uid} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {user.avatarUrl ? (
                                <img src={user.avatarUrl} alt={user.displayName || ''} className="h-10 w-10 rounded-full object-cover" />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-[#1A6B8A] flex items-center justify-center text-white font-medium">
                                  {(user.displayName || user.email || 'S')[0].toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-gray-900">{user.displayName || user.email}</span>
                                {isSelf && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-500">You</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[user.role ?? ''] ?? 'bg-gray-100 text-gray-600'}`}>
                            {ROLE_LABELS[user.role ?? ''] ?? user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isDeactivated ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Deactivated</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Active</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {canAct && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleDeactivate(user)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                                  isDeactivated
                                    ? 'text-green-600 border-green-200 hover:bg-green-50'
                                    : 'text-amber-600 border-amber-200 hover:bg-amber-50'
                                }`}
                              >
                                {isDeactivated ? 'Reactivate' : 'Deactivate'}
                              </button>
                              <button
                                onClick={() => handleDeleteStaff(user)}
                                className="px-3 py-1.5 text-xs font-semibold text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors btn-press"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invitations list */}
      <h2 className="text-[14px] font-bold text-[#111827] mb-3">Invitations</h2>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#9CA3AF] text-sm">Loading…</div>
        ) : invitations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="text-3xl mb-3">👥</div>
            <p className="text-[13px] text-[#6B7280]">No staff invitations yet</p>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[#F3F4F6]">
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Email</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Invited by</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.id} className="border-b border-[#F9FAFB] last:border-0 hover:bg-[#FAFAFA] transition-colors">
                  <td className="px-5 py-3.5 font-medium text-[#111827]">{inv.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${ROLE_COLORS[inv.role] ?? 'bg-gray-100 text-gray-600'}`}>
                      {ROLE_LABELS[inv.role] ?? inv.role}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLE[inv.status] || 'bg-gray-100 text-gray-500'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-[#6B7280]">{inv.invitedByName}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      {inv.status === 'pending' && (
                        <>
                          <button
                            onClick={() => copyLink(inv.token)}
                            className="px-3 py-1.5 text-[11px] font-semibold text-[#1A6B8A] border border-[#E4E4E2] rounded-lg hover:bg-[#EFF6FF] transition-colors"
                          >
                            Copy link
                          </button>
                          <button
                            onClick={() => handleSendEmail(inv)}
                            className="px-3 py-1.5 text-[11px] font-semibold text-[#1A6B8A] border border-[#E4E4E2] rounded-lg hover:bg-[#EFF6FF] transition-colors btn-press"
                          >
                            Send email
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(inv)}
                        className="px-3 py-1.5 text-[11px] font-semibold text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors btn-press"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
